import { Head, Link, usePoll } from "@inertiajs/react"
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  DollarSign,
  Zap,
} from "lucide-react"
import { useEffect, useState } from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import AppLayout from "@/layouts/app-layout"
import { agentRunsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface ToolCall {
  id: number
  tool_name: string
  input_params: Record<string, unknown> | null
  output_result: Record<string, unknown> | null
  status: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  prompt_tokens: number | null
  completion_tokens: number | null
  total_tokens: number | null
  estimated_cost: number | null
}

interface AgentError {
  id: number
  error_type: string
  error_message: string
  stack_trace: string | null
  context: string | null
  resolution_status: string
  created_at: string
}

interface AgentRun {
  id: number
  agent_type: string
  action: string
  status: string
  input_params: Record<string, unknown> | null
  result_summary: string | null
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  prompt_tokens: number | null
  completion_tokens: number | null
  total_tokens: number | null
  estimated_cost: number | null
  trace_id: string | null
  finish_reason: string | null
  last_message: string | null
}

interface Props {
  run: AgentRun
  tool_calls: ToolCall[]
  errors: AgentError[]
}

export default function AgentRunShow({ run, tool_calls, errors }: Props) {
  const [expandedTools, setExpandedTools] = useState<Set<number>>(
    new Set(tool_calls.map((tc) => tc.id)),
  )

  const { start, stop } = usePoll(
    2000,
    { only: ["run", "tool_calls", "errors"] },
    { autoStart: false },
  )

  useEffect(() => {
    if (run.status === "running") {
      start()
      return () => stop()
    }
  }, [run.status, start, stop])

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Agent Runs", href: agentRunsPath() },
    { title: `Run #${run.id}`, href: "#" },
  ]

  const toggleTool = (toolId: number) => {
    setExpandedTools((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(toolId)) {
        newSet.delete(toolId)
      } else {
        newSet.add(toolId)
      }
      return newSet
    })
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      completed:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
      failed: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
      running:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
    }

    const icons = {
      completed: <CheckCircle className="h-3 w-3" />,
      failed: <AlertCircle className="h-3 w-3" />,
      running: <Clock className="h-3 w-3" />,
      pending: <Clock className="h-3 w-3" />,
    }

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
          styles[status as keyof typeof styles]
        }`}
      >
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-"
    return `${seconds.toFixed(2)}s`
  }

  const formatCost = (cost: number | null) => {
    if (!cost) return "-"
    return `$${cost.toFixed(4)}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleString()
  }

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Agent Run #${run.id}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              href={agentRunsPath()}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{run.action}</h1>
                {getStatusBadge(run.status)}
              </div>
              <p className="text-muted-foreground mt-1">
                {run.agent_type} • Run #{run.id}
              </p>
            </div>
          </div>
          {run.trace_id && (
            <button
              onClick={() => copyToClipboard(run.trace_id!)}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
            >
              <Copy className="h-4 w-4" />
              Trace ID: {run.trace_id.substring(0, 8)}...
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="bg-card rounded-xl border p-4">
            <div className="text-muted-foreground text-sm">Duration</div>
            <p className="mt-1 text-2xl font-bold">
              {formatDuration(run.duration_seconds)}
            </p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Zap className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm">
                Total Tokens
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold">
              {run.total_tokens?.toLocaleString() ?? 0}
            </p>
            {run.prompt_tokens !== null && run.completion_tokens !== null && (
              <p className="text-muted-foreground mt-1 text-xs">
                {run.prompt_tokens} prompt + {run.completion_tokens} completion
              </p>
            )}
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm">
                Estimated Cost
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold">
              {formatCost(run.estimated_cost)}
            </p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Clock className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm">Tool Calls</span>
            </div>
            <p className="mt-1 text-2xl font-bold">{tool_calls.length}</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Started At</h3>
            <p className="text-sm">{formatDate(run.started_at)}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Completed At</h3>
            <p className="text-sm">{formatDate(run.completed_at)}</p>
          </div>
        </div>

        {run.input_params && (
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Input Parameters</h3>
            <pre className="bg-muted max-w-full overflow-x-auto rounded-lg p-3 text-xs">
              {JSON.stringify(run.input_params, null, 2)}
            </pre>
          </div>
        )}

        {run.result_summary && (
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Result Summary</h3>
            <p className="text-sm whitespace-pre-wrap">{run.result_summary}</p>
          </div>
        )}

        {run.finish_reason && (
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Finish Reason</h3>
            <p className="text-sm">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                {run.finish_reason}
              </span>
            </p>
          </div>
        )}

        {run.last_message && (
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Last Message</h3>
            <div className="text-sm break-words whitespace-pre-wrap">
              {run.last_message}
            </div>
          </div>
        )}

        {tool_calls.length > 0 && (
          <div className="rounded-xl border">
            <div className="border-b p-4">
              <h2 className="font-semibold">Tool Calls Timeline</h2>
            </div>

            <div className="divide-y">
              {tool_calls.map((tc, index) => (
                <Collapsible
                  key={tc.id}
                  open={expandedTools.has(tc.id)}
                  onOpenChange={() => toggleTool(tc.id)}
                >
                  <div className="p-4">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded text-xs font-semibold">
                            {index + 1}
                          </div>
                          <span className="font-medium">{tc.tool_name}</span>
                          {getStatusBadge(tc.status)}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-muted-foreground flex gap-4 text-xs">
                            <span>
                              Duration: {formatDuration(tc.duration_seconds)}
                            </span>
                            <span>Tokens: {tc.total_tokens ?? 0}</span>
                            <span>Cost: {formatCost(tc.estimated_cost)}</span>
                          </div>
                          {expandedTools.has(tc.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      {tc.input_params && (
                        <div className="mt-3">
                          <p className="text-muted-foreground mb-1 text-xs font-medium">
                            Input:
                          </p>
                          <pre className="bg-muted max-w-full overflow-x-auto rounded-lg p-2 text-xs">
                            {JSON.stringify(tc.input_params, null, 2)}
                          </pre>
                        </div>
                      )}

                      {tc.output_result && (
                        <div className="mt-3">
                          <p className="text-muted-foreground mb-1 text-xs font-medium">
                            Output:
                          </p>
                          <pre className="bg-muted max-w-full overflow-x-auto rounded-lg p-2 text-xs">
                            {JSON.stringify(tc.output_result, null, 2)}
                          </pre>
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20">
            <div className="border-b border-red-200 p-4 dark:border-red-900">
              <h2 className="flex items-center gap-2 font-semibold text-red-800 dark:text-red-300">
                <AlertCircle className="h-5 w-5" />
                Errors ({errors.length})
              </h2>
            </div>

            <div className="divide-y divide-red-200 dark:divide-red-900">
              {errors.map((error) => (
                <div key={error.id} className="p-4">
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-red-800 dark:text-red-300">
                        {error.error_type}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {error.error_message}
                      </p>
                    </div>
                    <span className="text-xs text-red-600 dark:text-red-400">
                      {error.resolution_status}
                    </span>
                  </div>

                  {error.stack_trace && (
                    <pre className="mt-3 max-w-full overflow-x-auto rounded-lg bg-red-100 p-3 text-xs text-red-900 dark:bg-red-950/40 dark:text-red-200">
                      {error.stack_trace}
                    </pre>
                  )}

                  {error.context && (
                    <div className="mt-3">
                      <p className="text-muted-foreground mb-1 text-xs font-medium">
                        Context:
                      </p>
                      <pre className="max-w-full overflow-x-auto rounded-lg bg-red-100 p-2 text-xs text-red-900 dark:bg-red-950/40 dark:text-red-200">
                        {JSON.stringify(error.context, null, 2)}
                      </pre>
                    </div>
                  )}

                  <p className="text-muted-foreground mt-3 text-xs">
                    {formatDate(error.created_at)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
